import React, { useState } from "react";
import DateControl from "../../../components/Fields/DateControl";
import Field from "../../../components/Fields/Field";
import FieldLabel from "../../../components/Fields/FieldLabel";
import SelectListField from "../../../components/Fields/SelectListField";
import TextboxField from "../../../components/Fields/TextboxField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import SavePopup from "../../../components/Popups/SavePopup";
import { useNotifications } from "../../../contexts/notificationContext";
import DataService from "../../../services/data.service";
import { BusinessPartnerPopupProps } from "../../../types/PopupProps";
import { DataResult } from "../../../types/Result";
import FormatUtils from "../../../utils/FormatUtils";

interface AddressCreateModel {
    businessPartnerId: number
    addressTypeId?: number
    addressLine1?: string
    addressLine2?: string
    addressLine3?: string
    zipCode?: string
    city?: string
    countryName?: string
    beginDate: string
    endDate?: string
    referenceCode?: string
    countryId?: number
}

const AddAddressPopup: React.FC<BusinessPartnerPopupProps> = ({ show, onHide, businessPartnerId }) => {
    const addressTypes = useSelectListOptions('api/v1/selectlist/AddressTypes', '-- Select address type');
    const countries = useSelectListOptions('api/v1/selectlist/Countries', '-- Select country');
    const { handleResult, handleException } = useNotifications();
    const [serverErrors, setServerErrors] = useState<DataResult<AddressCreateModel>>({ isSuccesful: true, messages: [], data: undefined });

    const [formData, setFormData] = useState<AddressCreateModel>({
        businessPartnerId: businessPartnerId,
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
    });

    function submit() {
        if (formData) {
            DataService
                .create<AddressCreateModel>(`api/v1/Addresses`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true) {
                        if (onHide)
                            onHide();
                    }
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'addressTypeId', label: 'Address type', labelSize: 3 },
            { name: 'addressLine1', label: 'Line 1', labelSize: 3 },
            { name: 'addressLine2', label: 'Line 2', labelSize: 3 },
            { name: 'addressLine3', label: 'Line 3', labelSize: 3 },
            { name: 'zipCode', label: 'Zipcode', labelSize: 3 },
            { name: 'city', label: 'City', labelSize: 3 },
            { name: 'countryId', label: 'Country', labelSize: 3 },
            { name: 'beginDate', label: 'Active from', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    return (
        <SavePopup show={show} onHide={onHide} size='lg' title='Add address' saveButtonProps={getSubmitButtonProps()}>
            <SelectListField {...getFieldProps('addressTypeId')} options={addressTypes} />
            <TextboxField {...getFieldProps('addressLine1')} />
            <TextboxField {...getFieldProps('addressLine2')} />
            <TextboxField {...getFieldProps('addressLine3')} />
            <TextboxField {...getFieldProps('zipCode')} />
            <TextboxField {...getFieldProps('city')} />
            <SelectListField {...getFieldProps('countryId')} options={countries} />
            <Field {...getLabelProps('beginDate')}>
                <DateControl {...getControlProps('beginDate')} />
                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                <DateControl {...getControlProps('endDate')} />
            </Field>
        </SavePopup>
    );
}
export default AddAddressPopup;